import {useEffect, useState} from 'react';
import * as yup from 'yup';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';

import TextArea from '../Textarea';
import Button from '../Button';
import Modal from '../Modal';
import Input from '../Input';

import IVillageSideColumnDictionary from '@/dictionaries/components/villageSideColumn/types';
import {IContactPageFormDictionary} from '@/dictionaries/pages/contacts/types';
import {postShareOneWithFriends} from '@/actions/shareWithFriends';
import {removeBodyScrollbar} from '@/utils/helpers';
import EmailSentModal from './EmailSentModal';

import './styles.css';

type Props = {
  formDict: IContactPageFormDictionary;
  villageSideColumnDict: IVillageSideColumnDictionary;
  id: number;
  type: 'villas' | 'experiences';
};

const Loader = () => {
  return (
    <div className="absolute w-full h-full flex items-center justify-center bg-[hsla(0,0%,100%,.6)]">
      <svg
        viewBox="0 0 24 24"
        className="w-12 h-[3rem]"
        xmlns="http://www.w3.org/2000/svg">
        <circle fill="#ebbf20" cx="12" cy="12" r="0">
          <animate
            attributeName="r"
            calcMode="spline"
            dur="1.2s"
            values="0;11"
            keySplines=".52,.6,.25,.99"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            calcMode="spline"
            dur="1.2s"
            values="1;0"
            keySplines=".52,.6,.25,.99"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    </div>
  );
};

export default function VillageSideColumnEmailModal({
  formDict,
  villageSideColumnDict,
  id,
  type,
}: Props) {
  const {
    errors: dictErrors,
    success: successModalDict,
    ...dict
  } = villageSideColumnDict.emailModal;

  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [hasError, setHasError] = useState(false);

  const schema = yup.object().shape({
    email: yup.string().email(dictErrors.validEmail),
    message: yup.string(),
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    getValues,
    control,
    reset,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async (data) => {
    setHasError(false);

    if (emails.length === 0) {
      return setHasError(true);
    }

    try {
      const response = await postShareOneWithFriends(
        {emails, message: data.message},
        id,
        type,
      );
      setIsLoading(true);

      if (typeof response === 'boolean' && response) {
        const intervalId = setInterval(() => {
          setModalIsOpen(false);
          setIsLoading(false);
          setShowSuccessModal(true);
          clearInterval(intervalId);
        }, 5000);
        reset();
        setEmails([]);
      } else {
        setIsLoading(false);
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  });

  async function addEmail() {
    clearErrors('email');
    const {errors} = await control._executeSchema(['email']);
    if (errors.email) {
      return setError('email', {message: dictErrors.validEmail});
    }
    const inputValue = getValues('email');
    const emailAlreadyExists = emails.find((email) => email === inputValue);
    if (emailAlreadyExists) {
      return setError('email', {message: dictErrors.alreadyAdded});
    }
    if (inputValue) {
      setEmails((prev) => [...prev, inputValue]);
      reset();
    }
  }

  function removeEmail(targetEmail: string) {
    const newEmails = emails.filter((email) => email !== targetEmail);
    setEmails(newEmails);
  }

  useEffect(() => {
    removeBodyScrollbar(modalIsOpen);
  }, [modalIsOpen]);

  return (
    <>
      {showSuccessModal && (
        <EmailSentModal
          dict={successModalDict}
          modalIsOpen={showSuccessModal}
          setModalIsOpen={setShowSuccessModal}
          type={type}
        />
      )}
      <Modal.Root open={modalIsOpen} onOpenChange={setModalIsOpen}>
        <Modal.Trigger>
          <div className="text-center cursor-pointer">
            <span
              className="text-gray-700 text-xs uppercase"
              title="How to book">
              {villageSideColumnDict.mailToAFriend}
            </span>
          </div>
        </Modal.Trigger>
        <Modal.Content
          contentCustomClass="md:!mb-[100px] md:mt-[10vh] md:!w-1/2 desktop:my-0"
          onOpenChange={setModalIsOpen}>
          <div className="overflow-y-auto md:mx-auto max-w-[1050px] relative">
            {isLoading && <Loader />}
            <div className="p-[30px]">
              <div className="text-center">
                <h2 className="mt-4 mb-2 text-4xl font-semibold">
                  {type === 'villas' ? dict.title : dict.titleExperience}
                </h2>
                <p className="mb-4">
                  {type === 'villas' ? dict.subtitle : dict.subtitleExperience}
                </p>
              </div>
              <form onSubmit={onSubmit}>
                <div className="mx-[-0.625rem] md:mx-[-0.9375rem] flex flex-wrap">
                  <div className="mb-4 desktop:mb-0 w-[calc(100%-1.25rem)] mx-[0.625rem] md:w-[calc(100%-1.875rem)] md:mx-[0.9375rem] desktop:w-[calc(50%-1.875rem)] flex-[0_0_auto]">
                    <Input.Container>
                      <Input.Label htmlFor="email">
                        {dict.inputLabel}
                      </Input.Label>
                      <div className="flex flex-[0_0_auto] whitespace-nowrap">
                        <Input.Root register={register} name="email" />
                        <button
                          type="button"
                          onClick={addEmail}
                          className="w-[45px] h-[2.5625rem] min-h-[44px] bg-white border border-underline">
                          <span className="text-3xl text-blue">+</span>
                        </button>
                      </div>
                      <Input.Error error={errors.email} />
                    </Input.Container>
                    {emails.length > 0 && (
                      <ul className="mb-4 mt-4 list-none">
                        {emails.map((email) => (
                          <li key={email} className="mb-2 flex items-center">
                            <button onClick={() => removeEmail(email)}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5 fill-current mr-2 text-gray-600"
                                viewBox="0 -960 960 960">
                                <path d="m256-168-88-88 224-224-224-224 88-88 224 224 224-224 88 88-224 224 224 224-88 88-224-224-224 224Z" />
                              </svg>
                            </button>
                            <span className="text-sm">{email}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="w-[calc(100%-1.25rem)] mx-[0.625rem] md:w-[calc(100%-1.875rem)] md:mx-[0.9375rem] desktop:w-[calc(50%-1.875rem)] flex-[0_0_auto]">
                    <TextArea
                      className="w-full"
                      inputClassName="w-full desktop:max-w-[443px] h-64"
                      register={register}
                      name="message"
                      label={dict.textAreaLabel}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-8 mt-4">
                  {hasError && (
                    <span className="text-warning">{dictErrors.emailMin}</span>
                  )}
                  <div className="flex flex-col-reverse md:flex-row items-end gap-4">
                    <Button
                      type="button"
                      onClick={() => setModalIsOpen(false)}
                      variant="outline"
                      className="w-[8rem]">
                      {dict.cancel}
                    </Button>
                    <Button className="w-[7rem]">{dict.send}</Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
